import type { AxiosRequestConfig } from 'axios';
import rpcClients from './rpc/rpcClients';
import rpcTasks from './rpc/rpcTasks';
import rpcAuth from './rpc/rpcAuth';

// NOTE: All endpoints and methods here mirror existing usage in the app.
// We do NOT change URLs, HTTP verbs, or payload/response shapes.

export interface ClientsQueryParams {
  page?: number;
  pageSize?: number;
  [key: string]: any;
}

/**
 * GET /api/:company/admin/clients
 * Used in Clients.tsx and prefetch/api.ts (fx.clients)
 * Now uses RPC: /api/:company/rpc/clients/get-admin-clients
 */
export async function getAdminClients(
  company: string,
  params?: ClientsQueryParams,
  config?: AxiosRequestConfig
) {
  const { data } = await rpcClients.getAdminClients(params);
  // RPC returns { ok: true, data: [...], pagination: {...} } or { ok: false, error }
  if (data.ok) {
    // Return full response including pagination if available
    // Backend returns: { ok: true, data: [...], pagination: {...} }
    if (data.pagination) {
      return {
        data: data.data || [],
        pagination: data.pagination
      };
    }
    // Legacy format: just return data array (pagination will be calculated on frontend)
    return data.data;
  }
  throw new Error(data.error || 'Failed to get admin clients');
}

/**
 * GET /api/:company/admin/client/:clientId
 * Thin wrapper used anywhere we fetch a single admin client by ID (e.g. AdminOrdersPage, Slots, etc.)
 * Now uses RPC: /api/:company/rpc/clients/get-by-id
 */
export async function getAdminClientById(clientId: string) {
  const { data } = await rpcClients.getById(clientId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get client');
}

/**
 * POST /api/:company/admin/clients/import/upload?lang=he
 * Used in Clients.tsx ImportModal.handleFile
 * Now uses RPC: /api/:company/rpc/clients/upload-clients-import
 */
export async function uploadClientsImport(
  company: string,
  formData: FormData,
  lang: string = 'he'
) {
  const { data } = await rpcClients.uploadClientsImport(formData, lang);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to upload clients import');
}

/**
 * POST /api/:company/admin/clients/import/dry-run
 * Used in Clients.tsx ImportModal.runDry
 * Now uses RPC: /api/:company/rpc/clients/run-clients-import-dry-run
 */
export async function runClientsImportDryRun(
  company: string,
  body: { uploadId: string; mapping: any; options: any }
) {
  const { data } = await rpcClients.runClientsImportDryRun(body.uploadId, body.mapping, body.options);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to run clients import dry run');
}

/**
 * POST /api/:company/admin/clients/import/commit
 * Used in Clients.tsx ImportModal.runCommit
 * Now uses RPC: /api/:company/rpc/clients/commit-clients-import
 */
export async function commitClientsImport(
  company: string,
  uploadId: string
) {
  const { data } = await rpcClients.commitClientsImport(uploadId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to commit clients import');
}

/**
 * PUT /api/:company/admin/clients/:id/password
 * Used in ClientProfile.tsx when updating client password
 * Now uses RPC: /api/:company/rpc/clients/update-client-password
 */
export async function updateClientPassword(
  company: string,
  clientId: string,
  newPassword: string
) {
  const { data } = await rpcClients.updateClientPassword(clientId, newPassword);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to update password');
}

/**
 * GET /api/:company/auth/me
 * Used in ClientContext and ClientSettings bootstrap.
 * Now uses RPC: /api/:company/rpc/auth/get-auth-me
 */
export async function getAuthMe(company: string) {
  // RPC returns the client data directly (wrapped in { ok, data })
  const client = await rpcAuth.getAuthMe(company);
  // Return client directly - ClientContext handles both { client } and direct client
  return client;
}

// ---------------- Client Settings (profile, email, phone, 2FA, login alerts) ----------------

/** POST /api/:company/client/send-email-change-otp */
/** Now uses RPC: /api/:company/rpc/clients/send-email-change-otp */
export async function sendEmailChangeOtp(company: string, newEmail: string) {
  const { data } = await rpcClients.sendEmailChangeOtp(newEmail);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to send email change OTP');
}

/** PUT /api/:company/client/change-email */
/** Now uses RPC: /api/:company/rpc/clients/change-client-email */
export async function changeClientEmail(company: string, payload: { newEmail: string; otp: string }) {
  const { data } = await rpcClients.changeClientEmail(payload);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to change client email');
}

/** PUT /api/:company/client/update-phone */
/** Now uses RPC: /api/:company/rpc/clients/update-client-phone */
export async function updateClientPhone(company: string, phone: string) {
  const { data } = await rpcClients.updateClientPhone(phone);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to update client phone');
}

/** POST /api/:company/client/send-2fa-setup-otp */
/** Now uses RPC: /api/:company/rpc/clients/send-2fa-setup-otp */
export async function send2FASetupOtp(company: string, method: 'email' | 'sms' = 'email') {
  const { data } = await rpcClients.send2FASetupOtp({ method });
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to send 2FA setup OTP');
}

/** PUT /api/:company/client/toggle-2fa */
/** Now uses RPC: /api/:company/rpc/clients/toggle-2fa */
export async function toggle2FA(company: string, payload: { enabled: boolean; otp?: string }) {
  const { data } = await rpcClients.toggle2FA(payload);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to toggle 2FA');
}

/** PUT /api/:company/client/toggle-login-alert */
/** Now uses RPC: /api/:company/rpc/clients/toggle-login-alert */
export async function toggleLoginAlert(company: string, enabled: boolean) {
  try {
    const response = await rpcClients.toggleLoginAlert(enabled);
    const data = response?.data || response;
    // RPC returns { ok: true, data } or { ok: false, error }
    if (data?.ok) {
      return data.data || data;
    }
    throw new Error(data?.error || 'Failed to toggle login alert');
  } catch (error: any) {
    // If it's already an Error, re-throw it
    if (error instanceof Error) {
      throw error;
    }
    // Otherwise, wrap it
    throw new Error(error?.message || error?.response?.data?.error || 'Failed to toggle login alert');
  }
}

// ---------------- Client Orders (client-facing) ----------------

/** GET /api/:company/client/orders */
/** Now uses RPC: /api/:company/rpc/clients/get-client-orders */
export async function getClientOrders(company: string) {
  const data = await rpcClients.getClientOrders();
  return Array.isArray(data) ? data : [];
}

/** POST /api/:company/client/orders/:orderId/accept */
/** Now uses RPC: /api/:company/rpc/clients/accept-client-order */
export async function acceptClientOrder(company: string, orderId: string) {
  const { data } = await rpcClients.acceptClientOrder(orderId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to accept client order');
}

/** POST /api/:company/admin/orders/:orderId/cancel (used when client cancels) */
/** Now uses RPC: /api/:company/rpc/clients/cancel-order-as-client */
export async function cancelOrderAsClient(company: string, orderId: string) {
  const { data } = await rpcClients.cancelOrderAsClient(orderId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to cancel order');
}

// ---------------- Orders (admin side used from client components) ----------------

/** GET /api/:company/admin/orders?clientId=... */
/** Now uses RPC: /api/:company/rpc/clients/get-admin-orders-for-client */
export async function getAdminOrdersForClient(company: string, clientId: string) {
  const data = await rpcClients.getAdminOrdersForClient(clientId);
  return Array.isArray(data) ? data : [];
}

/** Generic helper for admin order state transitions via specific endpoints */
/** Now uses RPC: /api/:company/rpc/clients/post-admin-order-action */
export async function postAdminOrderAction(
  company: string,
  orderId: string,
  path: string,
  body?: any
) {
  const { data } = await rpcClients.postAdminOrderAction(orderId, path, body);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to perform admin order action');
}

/** POST /api/:company/admin/orders (create order) */
/** Now uses RPC: /api/:company/rpc/clients/create-admin-order */
export async function createAdminOrder(company: string, payload: any) {
  const { data } = await rpcClients.createAdminOrder(payload);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to create admin order');
}

// ---------------- Clients list reused from CreateOrderModal ----------------

/** GET /api/:company/admin/clients (reused by CreateOrderModal) */
/** Now uses RPC: /api/:company/rpc/clients/get-admin-clients */
export async function getAdminClientsForOrders(company: string) {
  const { data } = await rpcClients.getAdminClients();
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get admin clients');
}

/**
 * Convenience alias used across pages that only need a simple list.
 * GET /api/:company/admin/clients
 * Now uses RPC: /api/:company/rpc/clients/get-admin-clients
 */
export async function getAdminClientsSimple(
  company: string,
  config?: AxiosRequestConfig
) {
  // Extract params from config if provided
  const params = config && 'params' in config ? config.params : undefined;
  const { data } = await rpcClients.getAdminClients(params);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get admin clients');
}

// ---------------- Client "me" profile helpers ----------------

/** GET /api/:company/client/me */
/** Now uses RPC: /api/:company/rpc/clients/get-client-me */
export async function getClientMe(company: string) {
  const { data } = await rpcClients.getClientMe();
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get client me');
}

/** PUT /api/:company/client/change-password */
/** Now uses RPC: /api/:company/rpc/clients/change-client-password */
export async function changeClientPassword(
  company: string,
  payload: { currentPassword?: string; newPassword: string; [key: string]: any }
) {
  const { data } = await rpcClients.changeClientPassword(payload);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to change client password');
}

// ---------------- Admin: Client Custom Fields (per client) ----------------

/** GET /api/:company/admin/client/:clientId/custom-fields */
/** Now uses RPC: /api/:company/rpc/clients/get-custom-fields */
export async function getAdminClientCustomFields(
  company: string,
  clientId: string
) {
  const { data } = await rpcClients.getCustomFields(clientId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data || [];
  }
  throw new Error(data.error || 'Failed to get custom fields');
}

/** POST /api/:company/admin/client/:clientId/custom-fields */
/** Now uses RPC: /api/:company/rpc/clients/create-custom-field */
export async function createAdminClientCustomField(
  company: string,
  clientId: string,
  payload: any
) {
  const { data } = await rpcClients.createCustomField(clientId, payload);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to create custom field');
}

/** PUT /api/:company/admin/client/:clientId/custom-fields/:id */
/** Now uses RPC: /api/:company/rpc/clients/update-custom-field */
export async function updateAdminClientCustomField(
  company: string,
  clientId: string,
  id: string,
  payload: any
) {
  const { data } = await rpcClients.updateCustomField(clientId, id, payload);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to update custom field');
}

/** DELETE /api/:company/admin/client/:clientId/custom-fields/:id */
/** Now uses RPC: /api/:company/rpc/clients/delete-custom-field */
export async function deleteAdminClientCustomField(
  company: string,
  clientId: string,
  id: string
) {
  const { data } = await rpcClients.deleteCustomField(clientId, id);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to delete custom field');
}

// ---------------- Manager: Clients & Notes ----------------

/** GET /api/:company/manager/clients */
/** Now uses RPC: /api/:company/rpc/clients/get-manager-clients */
export async function getManagerClients(company: string) {
  const { data } = await rpcClients.getManagerClients();
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get manager clients');
}

/** GET /api/:company/manager/client/:clientId */
/** Now uses RPC: /api/:company/rpc/clients/get-manager-client-by-id */
export async function getManagerClientById(company: string, clientId: string) {
  const { data } = await rpcClients.getManagerClientById(clientId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get manager client by id');
}

/** PUT /api/:company/manager/client/:clientId */
/** Now uses RPC: /api/:company/rpc/clients/update-manager */
export async function updateManagerClient(clientId: string, payload: any) {
  const { data } = await rpcClients.updateManager(clientId, payload);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return { client: data.data };
  }
  throw new Error(data.error || 'Failed to update client');
}

/** PUT /api/:company/agent/client/:clientId (or similar agent endpoint) */
/** Now uses RPC: /api/:company/rpc/clients/update-agent */
export async function updateAgentClient(clientId: string, payload: any) {
  const { data } = await rpcClients.updateAgent(clientId, payload);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return { client: data.data };
  }
  throw new Error(data.error || 'Failed to update client');
}

/** GET /api/:company/manager/client/:clientId/notes */
/** Now uses RPC: /api/:company/rpc/clients/notes/list */
export async function getManagerClientNotes(clientId: string) {
  const data = await rpcClients.notesList(clientId);
  return Array.isArray(data) ? data : [];
}

/** POST /api/:company/manager/client/:clientId/notes */
/** Now uses RPC: /api/:company/rpc/clients/notes/create */
export async function addManagerClientNote(clientId: string, text: string) {
  const { data } = await rpcClients.notesCreate(clientId, text);
  // RPC returns { ok: true, data: { notes, message } } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to add note');
}

/** PUT /api/:company/manager/client/:clientId/notes/:idx */
/** Now uses RPC: /api/:company/rpc/managers/clients/notes/update */
export async function updateManagerClientNote(
  clientId: string,
  noteIdx: number,
  text: string
) {
  // Use managers RPC endpoint for manager-specific note updates
  const rpcManagers = (await import('./rpc/rpcManagers')).default;
  const { data } = await rpcManagers.updateClientNote(clientId, noteIdx, text);
  // RPC returns { ok: true, data: { notes, message } } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to update note');
}

/** DELETE /api/:company/manager/client/:clientId/notes/:idx */
/** Now uses RPC: /api/:company/rpc/clients/notes/delete */
export async function deleteManagerClientNote(clientId: string, noteIdx: number) {
  const { data } = await rpcClients.notesDelete(clientId, noteIdx);
  // RPC returns { ok: true, data: { notes, message } } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to delete note');
}

// ---------------- Admin: Client Notes & Admin-only actions ----------------

/** POST /api/:company/admin/client/:clientId/notes */
/** Now uses RPC: /api/:company/rpc/clients/add-admin-client-note */
export async function addAdminClientNote(
  company: string,
  clientId: string,
  text: string
) {
  const { data } = await rpcClients.addAdminClientNote(clientId, text);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to add admin client note');
}

/** PUT /api/:company/admin/client/:clientId */
/** Now uses RPC: /api/:company/rpc/clients/update-admin */
export async function updateAdminClient(clientId: string, payload: any) {
  // DEBUG: Log API call
  if (process.env.NODE_ENV === 'development') {
    console.log('[STATUS SAVE] API call:', {
      clientId,
      payload,
      status: payload?.status,
      endpoint: 'rpcClients.updateAdmin',
    });
  }
  
  const { data } = await rpcClients.updateAdmin(clientId, payload);
  
  // DEBUG: Log API response
  if (process.env.NODE_ENV === 'development') {
    console.log('[STATUS SAVE] API response:', {
      ok: data?.ok,
      hasData: !!data?.data,
      clientStatus: data?.data?.status,
      fullResponse: JSON.stringify(data).substring(0, 500),
    });
  }
  
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return { client: data.data };
  }
  throw new Error(data.error || 'Failed to update client');
}

/** DELETE /api/:company/admin/client/:clientId */
/** Now uses RPC: /api/:company/rpc/clients/delete-admin-client */
export async function deleteAdminClient(
  company: string,
  clientId: string
) {
  const { data } = await rpcClients.deleteAdminClient(clientId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to delete client');
}

/** POST /api/:company/admin/client/:clientId/payments */
/** Now uses RPC: /api/:company/rpc/clients/create-client-payment */
export async function createAdminClientPayment(
  company: string,
  clientId: string,
  payload: any
) {
  const { data } = await rpcClients.createClientPayment(clientId, payload);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to create payment');
}

/** PUT /api/:company/admin/client/:clientId/renew */
/** Now uses RPC: /api/:company/rpc/clients/renew-client */
export async function renewAdminClient(
  company: string,
  clientId: string,
  payload: any
) {
  const { data } = await rpcClients.renewClient(clientId, payload);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to renew client');
}

/** PUT /api/:company/admin/client/:clientId/add-manager */
/** Now uses RPC: /api/:company/rpc/clients/add-manager */
export async function addManagerToAdminClient(clientId: string, managerId: string) {
  const { data } = await rpcClients.addManager(clientId, managerId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to add manager');
}

/** PUT /api/:company/admin/client/:clientId/remove-manager */
/** Now uses RPC: /api/:company/rpc/clients/remove-manager */
export async function removeManagerFromAdminClient(clientId: string, managerId: string) {
  const { data } = await rpcClients.removeManager(clientId, managerId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to remove manager');
}

/** PATCH/PUT /api/:company/admin/client/:clientId/block */
/** Now uses RPC: /api/:company/rpc/clients/block */
export async function setAdminClientBlocked(clientId: string, isBlocked: boolean) {
  const { data } = await rpcClients.block(clientId, isBlocked);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to update block status');
}

/** PUT /api/:company/admin/client/:clientId/convert-to-potential */
/** Now uses RPC: /api/:company/rpc/clients/convert-to-potential */
export async function convertAdminClientToPotential(clientId: string, agentId?: string) {
  const { data } = await rpcClients.convertToPotential(clientId, agentId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return { client: data.data };
  }
  throw new Error(data.error || 'Failed to convert client');
}

// ---------------- Admin: Agents' Clients ----------------

/** GET /api/:company/admin/agents/clients */
/** Now uses RPC: /api/:company/rpc/clients/get-admin-agents-clients */
export async function getAdminAgentsClients(
  company: string,
  params?: Record<string, any>
) {
  const { data } = await rpcClients.getAdminAgentsClients(params);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get admin agents clients');
}

/** GET /api/:company/admin/agents/clients/:id */
/** Now uses RPC: /api/:company/rpc/clients/get-admin-agent-client-by-id */
export async function getAdminAgentClientById(company: string, id: string) {
  const { data } = await rpcClients.getAdminAgentClientById(id);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get admin agent client by id');
}

/** PATCH or PUT /api/:company/admin/agents/clients/:id/block */
/** Now uses RPC: /api/:company/rpc/clients/set-admin-agent-client-blocked */
export async function setAdminAgentClientBlocked(
  company: string,
  id: string,
  isBlocked: boolean,
  method: 'patch' | 'put' = 'patch'
) {
  const { data } = await rpcClients.setAdminAgentClientBlocked(id, isBlocked, method);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to set admin agent client blocked');
}

/** PUT /api/:company/admin/agents/clients/:id/status */
/** Now uses RPC: /api/:company/rpc/clients/update-admin-agent-client-status */
export async function updateAdminAgentClientStatus(
  company: string,
  id: string,
  status: string
) {
  const { data } = await rpcClients.updateAdminAgentClientStatus(id, status);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to update admin agent client status');
}

/** PUT /api/:company/admin/agents/clients/:id/convert */
/** Now uses RPC: /api/:company/rpc/clients/convert-admin-agent-client */
export async function convertAdminAgentClient(
  company: string,
  id: string
) {
  const { data } = await rpcClients.convertAdminAgentClient(id);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to convert admin agent client');
}

/** PUT /api/:company/admin/agents/clients/:id/transfer */
/** Now uses RPC: /api/:company/rpc/clients/transfer-admin-agent-client */
export async function transferAdminAgentClient(
  company: string,
  id: string,
  newAgentId: string
) {
  const { data } = await rpcClients.transferAdminAgentClient(id, newAgentId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to transfer admin agent client');
}

/** DELETE /api/:company/admin/agents/clients/:id */
/** Now uses RPC: /api/:company/rpc/clients/delete-admin-agent-client */
export async function deleteAdminAgentClient(company: string, id: string) {
  const { data } = await rpcClients.deleteAdminAgentClient(id);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to delete admin agent client');
}

// ---------------- Inventory (used in CreateOrderModal) ----------------

/** GET /api/:company/admin/inventory */
/** Now uses RPC: /api/:company/rpc/clients/get-admin-inventory */
export async function getAdminInventory(company: string) {
  const { data } = await rpcClients.getAdminInventory();
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get admin inventory');
}

// ---------------- Client Contacts (used in ClientAttachments) ----------------

/** GET /api/:company/clients/:clientId/contacts */
/** Now uses RPC: /api/:company/rpc/clients/get-contacts */
export async function getClientContacts(company: string, clientId: string) {
  const { data } = await rpcClients.getContacts(clientId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data || [];
  }
  throw new Error(data.error || 'Failed to get contacts');
}

/** POST /api/:company/clients/:clientId/contacts */
/** Now uses RPC: /api/:company/rpc/clients/add-contact */
export async function addClientContact(
  company: string,
  clientId: string,
  payload: { name: string; email: string; note?: string }
) {
  const { data } = await rpcClients.addContact(clientId, payload);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to add contact');
}

/** DELETE /api/:company/clients/:clientId/contacts/:contactId */
/** Now uses RPC: /api/:company/rpc/clients/delete-contact */
export async function deleteClientContact(
  company: string,
  clientId: string,
  contactId: string
) {
  const { data } = await rpcClients.deleteContact(clientId, contactId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (!data.ok) {
    throw new Error(data.error || 'Failed to delete contact');
  }
}

// ---------------- Attachments & Invoices (used in ClientAttachments) ----------------

/** GET /api/:company/invoices/folders?clientId=... */
/** Now uses RPC: /api/:company/rpc/clients/get-invoice-folders */
export async function getInvoiceFolders(company: string, clientId: string) {
  const { data } = await rpcClients.getInvoiceFolders(clientId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get invoice folders');
}

/** GET /api/attachments/:company/:clientId/:folder */
/** Now uses RPC: /api/:company/rpc/clients/get-client-attachments */
export async function getClientAttachments(
  company: string,
  clientId: string,
  folder: string,
  params?: Record<string, string | undefined>
) {
  const { data } = await rpcClients.getClientAttachments(clientId, folder, params);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get client attachments');
}

/** POST /api/upload/:company/:clientId/:folder[/subfolder] */
/** Now uses RPC: /api/:company/rpc/clients/upload-client-attachment */
/** DEPRECATED: Use uploadClientAttachmentDirect for faster direct Cloudinary uploads */
export async function uploadClientAttachment(
  company: string,
  clientId: string,
  folder: string,
  formData: FormData,
  subfolder?: string,
  onUploadProgress?: (progressEvent: { loaded: number; total: number }) => void
) {
  const { data } = await rpcClients.uploadClientAttachment(clientId, folder, formData, subfolder, onUploadProgress);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to upload client attachment');
}

/** Direct upload to Cloudinary from frontend - MUCH FASTER */
export async function uploadClientAttachmentDirect(
  company: string,
  clientId: string,
  folder: string,
  file: File,
  title: string,
  subfolder?: string,
  onUploadProgress?: (progressEvent: { loaded: number; total: number }) => void
) {
  // Determine resource type
  const isPdf = file.type === 'application/pdf';
  const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';
  const resource_type = isPdf ? 'raw' : (file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'raw');
  const type = isPdf ? 'authenticated' : 'upload';

  // Get upload signature from backend
  const { data: sigData } = await rpcClients.getUploadSignature(folder, subfolder, resource_type, type);
  if (!sigData.ok) {
    throw new Error(sigData.error || 'Failed to get upload signature');
  }
  const { signature, timestamp, folder: cloudinaryFolder, cloud_name, api_key } = sigData.data;

  // Upload directly to Cloudinary
  const formData = new FormData();
  formData.append('file', file);
  formData.append('api_key', api_key);
  formData.append('timestamp', timestamp.toString());
  formData.append('signature', signature);
  formData.append('folder', cloudinaryFolder);
  formData.append('resource_type', resource_type);
  formData.append('type', type);

  const uploadUrl = `https://api.cloudinary.com/v1_1/${cloud_name}/${resource_type}/upload`;

  const response = await new Promise<any>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onUploadProgress) {
        onUploadProgress({
          loaded: e.loaded,
          total: e.total,
        });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (err) {
          reject(new Error('Invalid response from Cloudinary'));
        }
      } else {
        try {
          const error = JSON.parse(xhr.responseText);
          reject(new Error(error.error?.message || 'Upload failed'));
        } catch {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.open('POST', uploadUrl);
    xhr.send(formData);
  });

  // Save metadata to backend
  const { data: saveData } = await rpcClients.saveAttachmentMetadata({
    clientId,
    folder,
    subfolder,
    title,
    public_id: response.public_id,
    url: response.secure_url,
    bytes: response.bytes || file.size,
    format: response.format,
    mime: file.type,
    resource_type,
  });

  if (!saveData.ok) {
    // Try to delete from Cloudinary if save fails
    try {
      const deleteFormData = new FormData();
      deleteFormData.append('public_id', response.public_id);
      deleteFormData.append('api_key', api_key);
      deleteFormData.append('timestamp', timestamp.toString());
      deleteFormData.append('signature', signature);
      await fetch(`https://api.cloudinary.com/v1_1/${cloud_name}/${resource_type}/destroy`, {
        method: 'POST',
        body: deleteFormData,
      });
    } catch {}
    throw new Error(saveData.error || 'Failed to save attachment metadata');
  }

  return saveData.data;
}

/** DELETE /api/attachments/:company/:clientId/:pid */
/** Now uses RPC: /api/:company/rpc/clients/delete-client-attachment */
export async function deleteClientAttachment(
  company: string,
  clientId: string,
  publicId: string
) {
  const { data } = await rpcClients.deleteClientAttachment(clientId, publicId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to delete client attachment');
}

/** GET /api/:company/attachments/zip (responseType: blob) */
/** Now uses RPC: /api/:company/rpc/clients/download-attachments-zip */
export async function downloadAttachmentsZip(
  company: string,
  params: Record<string, any>
): Promise<Blob> {
  try {
    const response = await rpcClients.downloadAttachmentsZip(params);
    
    // For blob responses, response.data is the Blob itself
    if (response.data instanceof Blob) {
      // Check if blob is empty (likely an error)
      if (response.data.size === 0) {
        throw new Error('הקובץ שהורד ריק - לא נמצאו קבצים להורדה');
      }
      
      // If it's a small blob, it might be an error JSON response (404/400 errors are converted to blob)
      if (response.data.size < 1000) {
        // Try to read it as text to see if it's an error JSON
        const text = await response.data.text();
        try {
          const json = JSON.parse(text);
          if (json.ok === false || json.error) {
            throw new Error(json.error || 'לא נמצאו קבצים להורדה בהתאם למסננים שנבחרו');
          }
        } catch (parseErr) {
          // Not JSON, but still suspicious - check if it's actually empty
          if (text.trim().length === 0) {
            throw new Error('הקובץ שהורד ריק - לא נמצאו קבצים להורדה');
          }
          // Return the blob anyway if it has content
          return response.data;
        }
      }
      
      // Verify it's actually a ZIP file by checking the content type
      if (response.data.type === 'application/zip' || response.data.type === 'application/x-zip-compressed' || response.data.size > 0) {
        return response.data;
      }
      
      return response.data;
    }
    // If it's JSON (error response), parse it
    if (typeof response.data === 'object' && response.data.ok === false) {
      throw new Error(response.data.error || 'Failed to download attachments zip');
    }
    throw new Error('Invalid response format');
  } catch (err: any) {
    // Handle timeout errors silently
    // Silently handle timeout errors - don't throw (no toast will be shown)
    const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout') || err?.message?.includes('exceeded');
    if (isTimeout) {
      // Return empty blob instead of throwing - no error toast will be shown
      return new Blob();
    }
    
    // Handle error responses (404, 400, etc.) - when responseType is 'blob', axios converts JSON errors to blobs
    if (err?.response) {
      const status = err.response.status;
      const data = err.response.data;
      
      // If data is a blob (error JSON converted to blob), try to parse it
      if (data instanceof Blob && data.size < 1000) {
        try {
          const text = await data.text();
          const json = JSON.parse(text);
          if (json.ok === false || json.error) {
            throw new Error(json.error || 'לא נמצאו קבצים להורדה בהתאם למסננים שנבחרו');
          }
        } catch (parseErr) {
          // If parsing fails, use status-based error message
          if (status === 404) {
            throw new Error('לא נמצאו קבצים להורדה בהתאם למסננים שנבחרו');
          }
          if (status === 400) {
            throw new Error('פרמטרים חסרים או לא תקינים להורדת הקובץ');
          }
          throw new Error(`שגיאה בהורדת הקובץ (קוד ${status})`);
        }
      }
      
      // Handle 400 errors with better message
      if (status === 400) {
        const errorMsg = typeof data === 'object' && data.error ? data.error : 'פרמטרים חסרים להורדת הקובץ';
        throw new Error(errorMsg);
      }
      // Handle 404 errors
      if (status === 404) {
        const errorMsg = typeof data === 'object' && data.error ? data.error : 'לא נמצאו קבצים להורדה בהתאם למסננים שנבחרו';
        throw new Error(errorMsg);
      }
    }
    throw err;
  }
}

/** POST /api/:company/attachments/send-zip */
/** Now uses RPC: /api/:company/rpc/clients/send-attachments-zip */
export async function sendAttachmentsZip(
  company: string,
  payload: any
) {
  try {
    const { data } = await rpcClients.sendAttachmentsZip(payload);
    // RPC returns { ok: true, data } or { ok: false, error }
    if (data.ok) {
      return data.data;
    }
    throw new Error(data.error || 'Failed to send attachments zip');
  } catch (err: any) {
    // Silently handle timeout errors - don't throw (no toast will be shown)
    const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout') || err?.message?.includes('exceeded');
    if (isTimeout) {
      // Return silently - no error toast will be shown
      return;
    }
    // Handle 404 errors with better message
    if (err?.response?.status === 404) {
      const errorMsg = err?.response?.data?.error || 'לא נמצאו קבצים לשליחה';
      throw new Error(errorMsg);
    }
    // Handle 400 errors
    if (err?.response?.status === 400) {
      const errorMsg = err?.response?.data?.error || 'פרמטרים חסרים לשליחת המייל';
      throw new Error(errorMsg);
    }
    throw err;
  }
}

// ---------------- Bulk Operations (Clients.tsx) ----------------

/** GET /api/:company/admin/bulk/templates */
/** Now uses RPC: /api/:company/rpc/clients/get-bulk-templates */
export async function getBulkTemplates(
  company: string,
  kind: 'sms' | 'email'
) {
  const { data } = await rpcClients.getBulkTemplates(kind);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get bulk templates');
}

/** GET /api/:company/admin/features */
/** Now uses RPC: /api/:company/rpc/clients/get-admin-features */
export async function getAdminFeatures(company: string) {
  const { data } = await rpcClients.getAdminFeatures();
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get admin features');
}

/** GET /api/:company/admin/managers */
/** Now uses RPC: /api/:company/rpc/clients/get-admin-managers */
export async function getAdminManagers(company: string) {
  const { data } = await rpcClients.getAdminManagers();
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get admin managers');
}

/** GET /api/:company/manager/managers */
/** Now uses RPC: /api/:company/rpc/clients/get-manager-managers */
export async function getManagerManagers(company: string) {
  const { data } = await rpcClients.getManagerManagers();
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get manager managers');
}

/** GET /api/:company/admin/agents */
/** Now uses RPC: /api/:company/rpc/clients/get-admin-agents */
export async function getAdminAgents(company: string) {
  const { data } = await rpcClients.getAdminAgents();
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get admin agents');
}

/** GET /api/:company/admin/report/usage/export (blob) */
/** Now uses RPC: /api/:company/rpc/clients/export-usage-report */
/** Returns jobId - use pollExportJob and downloadExportFile to get the result */
export async function exportUsageReport(
  company: string,
  type: 'csv' | 'xlsx' | 'pdf'
): Promise<{ jobId: string }> {
  const response = await rpcClients.exportUsageReport(type);
  if (!response.ok || !response.data?.jobId) {
    throw new Error(response.error || 'Failed to start export job');
  }
  return { jobId: response.data.jobId };
}

/**
 * Poll export job status until completed or failed
 * @param onProgress Optional callback to report progress updates
 */
export async function pollExportJob(
  company: string, 
  jobId: string, 
  maxAttempts: number = 90, 
  intervalMs: number = 800,
  onProgress?: (progress: number, status: string) => void
): Promise<{ status: string; result?: { url: string; filename: string; mimetype: string }; error?: string }> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const response = await rpcClients.getJobStatus(jobId);
    if (!response.ok) {
      throw new Error(response.data?.error || 'Failed to get job status');
    }
    
    const { status, result, error, progress } = response.data;
    
    // Report progress if callback provided
    if (onProgress && progress !== undefined) {
      onProgress(progress, status);
    }
    
    if (status === 'completed') {
      return { status, result };
    }
    
    if (status === 'failed') {
      throw new Error(error || 'Export job failed');
    }
    
    // Wait before next poll (shorter interval for faster response)
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }
  
  throw new Error('Export job timed out');
}

/**
 * Download completed export file
 */
export async function downloadExportFile(company: string, jobId: string): Promise<Blob> {
  const response = await rpcClients.downloadJobFile(jobId);
  return response.data as Blob;
}

/** GET /api/:company/admin/sms-limit */
/** Now uses RPC: /api/:company/rpc/clients/get-sms-limit */
export async function getSmsLimit(company: string) {
  const { data } = await rpcClients.getSmsLimit();
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get SMS limit');
}

/** GET /api/:company/google/status */
/** Now uses RPC: /api/:company/rpc/clients/get-google-status */
export async function getGoogleStatus(company: string, params?: { adminId?: string; managerId?: string }) {
  const { data } = await rpcClients.getGoogleStatus(params);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get Google status');
}

/** POST /api/:company/admin/bulk/sms */
/** Now uses RPC: /api/:company/rpc/clients/send-bulk-sms */
export async function sendBulkSms(company: string, payload: any) {
  const { data } = await rpcClients.sendBulkSms(payload);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to send bulk SMS');
}

/** POST /api/:company/admin/bulk/email */
/** Now uses RPC: /api/:company/rpc/meet/admin/bulk-email */
export { sendBulkEmail } from './googleApi';

/** POST /api/:company/admin/templates */
/** Now uses RPC: /api/:company/rpc/clients/create-template */
export async function createTemplate(company: string, payload: {
  channel: 'sms' | 'email';
  title: string;
  body?: string;
  subject?: string;
}) {
  const { data } = await rpcClients.createTemplate(payload);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to create template');
}

/** POST /api/:company/auth/register */
/** Now uses RPC: /api/:company/rpc/auth/register */
export async function registerClient(company: string, payload: any) {
  // RPC returns { message, client }
  return await rpcAuth.register(payload, company);
}

// ---------------- Tasks & Reminders (ClientProfile.tsx) ----------------

/** POST /api/:company/admin/notify-admin */
/** Now uses RPC: /api/:company/rpc/clients/notify-admin */
export async function notifyAdmin(
  company: string,
  payload: { 
    clientId?: string; 
    managerName: string; 
    action: 'added' | 'removed' | 'task_note';
    timestamp?: string;
    // Task note specific fields
    taskId?: string;
    taskPublicId?: string;
    noteId?: string;
    noteBody?: string;
    taskTitle?: string;
    visibility?: 'all' | 'admin' | 'manager';
  }
) {
  const { data } = await rpcClients.notifyAdmin(payload);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (!data.ok) {
    throw new Error(data.error || 'Failed to notify admin');
  }
}

/** GET /api/:company/admin/task-meta */
/** Now uses RPC: /api/:company/rpc/admin/task-meta/get-task-meta */
export async function getTaskMeta(company: string) {
  const rpcTaskMeta = (await import('./rpc/rpcTaskMeta')).default;
  const { data } = await rpcTaskMeta.getTaskMeta();
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get task meta');
}

/** GET /api/:company/reminders/client/:clientId */
/** Now uses RPC: /api/:company/rpc/clients/get-client-reminders */
export async function getClientReminders(company: string, clientId: string) {
  const { data } = await rpcTasks.getClientReminders(clientId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get client reminders');
}

/** GET /api/:company/:rolePrefix/tasks?clientId=... */
/** Now uses RPC: /api/:company/rpc/clients/get-tasks-by-client */
export async function getTasksByClient(
  company: string,
  clientId: string,
  rolePrefix: 'admin' | 'manager'
) {
  const { data } = await rpcTasks.getTasksByClient(clientId, { rolePrefix });
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get tasks by client');
}

/** GET /api/:company/admin/tasks?clientId=... */
/** Now uses RPC: /api/:company/rpc/clients/get-admin-tasks-by-client */
export async function getAdminTasksByClient(company: string, clientId: string) {
  const { data } = await rpcTasks.getAdminTasksByClient(clientId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get admin tasks by client');
}

/** GET /api/:company/:rolePrefix/client/:clientId/tasks */
/** Now uses RPC: /api/:company/rpc/clients/get-client-tasks */
export async function getClientTasks(
  company: string,
  clientId: string,
  rolePrefix: 'admin' | 'manager'
) {
  const { data } = await rpcClients.getClientTasks(clientId, rolePrefix);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get client tasks');
}

/** PUT /api/:company/:rolePrefix/tasks/:taskId/status */
/** Now uses RPC: /api/:company/rpc/clients/update-task-status */
export async function updateTaskStatus(
  company: string,
  taskId: string,
  rolePrefix: 'admin' | 'manager',
  status: string
) {
  const { data } = await rpcClients.updateTaskStatus(taskId, rolePrefix, status);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to update task status');
}

/** PUT /api/:company/:rolePrefix/tasks/:taskId/transfer-manager */
/** Now uses RPC: /api/:company/rpc/clients/transfer-task-manager */
export async function transferTaskManager(
  company: string,
  taskId: string,
  rolePrefix: 'admin' | 'manager',
  managerId: string
) {
  const { data } = await rpcClients.transferTaskManager(taskId, rolePrefix, managerId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to transfer task manager');
}

/** GET /api/:company/:rolePrefix/task/:taskId */
/** Now uses RPC: /api/:company/rpc/clients/get-task-by-id */
export async function getTaskById(
  company: string,
  taskId: string,
  rolePrefix: 'admin' | 'manager'
) {
  const { data } = await rpcClients.getTaskById(taskId, rolePrefix);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get task by id');
}

/** GET /api/:company/admin/task/:taskId */
/** Now uses RPC: /api/:company/rpc/clients/get-admin-task-by-id */
export async function getAdminTaskById(company: string, taskId: string) {
  const { data } = await rpcClients.getAdminTaskById(taskId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get admin task by id');
}

/** GET /api/:company/reminders/:reminderId */
/** Now uses RPC: /api/:company/rpc/clients/get-reminder-by-id */
export async function getReminderById(company: string, reminderId: string) {
  const { data } = await rpcClients.getReminderById(reminderId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get reminder by id');
}

/** PUT /api/:company/reminders/:taskId */
/** Now uses RPC: /api/:company/rpc/clients/update-reminder */
export async function updateReminder(company: string, taskId: string, payload: any) {
  const { data } = await rpcClients.updateReminder(taskId, payload);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to update reminder');
}

/** PUT /api/:company/:rolePrefix/task/:taskId/complete */
/** Now uses RPC: /api/:company/rpc/clients/complete-task */
export async function completeTask(
  company: string,
  taskId: string,
  rolePrefix: 'admin' | 'manager'
) {
  const { data } = await rpcClients.completeTask(taskId, rolePrefix);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to complete task');
}

/** PUT /api/:company/admin/task/:taskId (complete) */
/** Now uses RPC: /api/:company/rpc/clients/complete-admin-task */
export async function completeAdminTask(company: string, taskId: string) {
  const { data } = await rpcClients.completeAdminTask(taskId);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to complete admin task');
}

/** POST /api/:company/reminders */
/** Now uses RPC: /api/:company/rpc/clients/create-reminder */
export async function createReminder(company: string, payload: any) {
  const { data } = await rpcClients.createReminder(payload);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to create reminder');
}

// ---------------- Client Settings (ClientSettings.tsx) ----------------

/** PUT /api/:company/client/preferences */
/** Now uses RPC: /api/:company/rpc/clients/update-client-preferences */
export async function updateClientPreferences(company: string, preferences: any) {
  const { data } = await rpcClients.updateClientPreferences(preferences);
  // RPC returns { ok: true, data } or { ok: false, error }
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to update client preferences');
}

